import type { Component } from "vue";
import { computed } from "vue";
import { useRoute } from "vue-router";
import { storeToRefs } from "pinia";
import { Eye, Pencil, Play } from "lucide-vue-next";
import { useCurrentUserStore } from "@/stores/currentUser";
import { useSurveyStore } from "@/stores/survey/survey";
import { useSurveyCardsStore } from "@/stores/survey/surveyCards";
import { useResultsV2Store } from "@/stores/survey/resultsV2/resultsV2";
import { userType } from "@utils/constants/userRoles";

interface HeaderTabItem {
  value: string;
  label: string;
  icon: Component;
}

interface HeaderTabsConfig {
  value: string;
  tabs: HeaderTabItem[];
  setValue: (_value: string) => void;
}

export function useAppHeaderTabs() {
  const route = useRoute();

  const currentUserStore = useCurrentUserStore();
  const { user } = storeToRefs(currentUserStore);

  const surveyStore = useSurveyStore();
  const { survey, canWriteSurvey } = storeToRefs(surveyStore);

  const surveyCardsStore = useSurveyCardsStore();
  const { activePane: surveyQuestionsActivePane } = storeToRefs(surveyCardsStore);
  const { setActivePane: setSurveyQuestionsActivePane } = surveyCardsStore;

  const hideQuestionsEditTab = computed(
    () =>
      !canWriteSurvey.value || (survey.value?.freemium_demo === true && user.value?.user_type === userType.CLIENT),
  );

  const resultsV2Store = useResultsV2Store();
  const { editMode: resultsV2EditMode } = storeToRefs(resultsV2Store);

  const tabsConfigByRouteName: Partial<Record<string, () => HeaderTabsConfig>> = {
    SurveyQuestions: () => ({
      value: surveyQuestionsActivePane.value,
      tabs: [
        !hideQuestionsEditTab.value ? { value: "Edit", label: "Edit", icon: Pencil } : null,
        { value: "Review", label: "Review", icon: Eye },
        { value: "Test", label: "Test", icon: Play },
      ].filter(Boolean) as HeaderTabItem[],
      setValue: setSurveyQuestionsActivePane,
    }),
    SurveyResultsV2: () => ({
      value: resultsV2EditMode.value ? "edit" : "view",
      tabs: [
        { value: "edit", label: "Edit", icon: Pencil },
        { value: "view", label: "View", icon: Eye },
      ],
      setValue: (mode: string) => resultsV2Store.setEditMode(mode === "edit"),
    }),
    SurveyResultsV2View: () => ({
      value: resultsV2EditMode.value ? "edit" : "view",
      tabs: [
        { value: "edit", label: "Edit", icon: Pencil },
        { value: "view", label: "View", icon: Eye },
      ],
      setValue: (mode: string) => resultsV2Store.setEditMode(mode === "edit"),
    }),
  };

  const tabsConfig = computed<HeaderTabsConfig | null>(() => {
    if (typeof route.name !== "string") {
      return null;
    }

    return tabsConfigByRouteName[route.name]?.() ?? null;
  });

  const hasTabs = computed(() => tabsConfig.value !== null);

  return {
    hasTabs,
    tabsConfig,
  };
}
